/**
 * AI Render modal — overlay that owns the request lifecycle for one
 * image-to-image generation against /api/realistic.
 *
 * Lifecycle:
 *   state.phase = 'pending' → spinner
 *   state.phase = 'done'    → <img> + Download + Regenerate
 *   state.phase = 'error'   → error message + Regenerate
 *
 * Snapshot capture and prompt build happen in the parent (configurator.jsx)
 * and arrive here via props.snapshotBase64 + props.prompt. Regenerate calls
 * back into the parent to capture a fresh snapshot.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, RefreshCw } from 'lucide-react';
import { t } from '../i18n.js';

export default function RenderModal({ snapshotBase64, prompt, onClose, onRegenerate, lang }) {
  const [phase, setPhase] = useState('pending');
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [modelUsed, setModelUsed] = useState(null);

  // AbortController per mount. Aborting on unmount cancels the in-flight
  // /api/realistic request — prevents both the React "setState on unmounted"
  // warning and burning a ~$0.04 Vertex generation that the user already left.
  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;

    (async () => {
      setPhase('pending');
      setError(null);
      setImageDataUrl(null);
      setModelUsed(null);
      try {
        const resp = await fetch('/api/realistic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: snapshotBase64, prompt }),
          signal: ctrl.signal,
        });
        if (!alive) return;
        const data = await resp.json().catch(() => ({}));
        if (!alive) return;
        if (!resp.ok) {
          setError(data?.error || `HTTP ${resp.status}`);
          setPhase('error');
          return;
        }
        const mime = data.mimeType || 'image/png';
        setImageDataUrl(`data:${mime};base64,${data.imageBase64}`);
        setModelUsed(data.modelUsed || null);
        setPhase('done');
      } catch (e) {
        if (!alive || e?.name === 'AbortError') return;
        setError(String(e?.message || e));
        setPhase('error');
      }
    })();

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [snapshotBase64, prompt]);

  // ESC closes
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const onDownload = useCallback(() => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = `scorpio-render-${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [imageDataUrl]);

  return (
    <div className="cfg-render-backdrop" onClick={onClose}>
      <div className="cfg-render-modal" onClick={e => e.stopPropagation()}>
        <div className="cfg-render-header">
          <span>{t('render.title', lang)}</span>
          <button className="cfg-render-x" onClick={onClose} aria-label="close">
            <X size={16} />
          </button>
        </div>
        <div className="cfg-render-body">
          {phase === 'pending' && (
            <div className="cfg-render-pending">
              <div className="cfg-spinner" />
              <div>{t('render.generating', lang)}</div>
              <div className="cfg-render-hint">{t('render.generating_hint', lang)}</div>
            </div>
          )}
          {phase === 'done' && imageDataUrl && (
            <img className="cfg-render-image" src={imageDataUrl} alt="AI render" />
          )}
          {phase === 'error' && (
            <div className="cfg-render-error">
              <div>{t('render.error', lang)}</div>
              <div className="cfg-render-error-detail">{error}</div>
            </div>
          )}
        </div>
        <div className="cfg-render-footer">
          {phase === 'done' && modelUsed && (
            <span className="cfg-render-model">{modelUsed}</span>
          )}
          <button className="cfg-btn" onClick={onRegenerate} disabled={phase === 'pending'}>
            <RefreshCw size={14} /> {t('render.regenerate', lang)}
          </button>
          {phase === 'done' && (
            <button className="cfg-btn cfg-btn-primary" onClick={onDownload}>
              <Download size={14} /> {t('render.download', lang)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
